// src/components/store/sections/hero-banner/DynamicHeroVariant.tsx
"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Sparkles, Flame, ShieldCheck, Star, Ticket, Zap, Award } from "lucide-react";

export default function DynamicHeroVariant({
  variantNumber,
  archKey,
  archLabel,
  palName,
  grad,
  lightBg,
  textCol,
  borderCol,
  btnCls,
  banners = [],
}: any) {
  const b = banners?.[0] || {};
  const title = b.title || `${archLabel} — ${palName} Collection`;
  const subtitle =
    b.subtitle ||
    "Experience curated premium quality products with express nationwide delivery and verified authenticity.";
  const badge = b.badge_title || `EDITION #${variantNumber} • ${archLabel.toUpperCase()}`;
  const btnText = b.button_text || "Explore Now";
  const targetUrl = b.target_url || "/products";

  const rawImg = b.image;
  const imgUrl = rawImg
    ? rawImg.startsWith("http")
      ? rawImg
      : `http://127.0.0.1:8000${rawImg.startsWith("/") ? "" : "/"}${rawImg}`
    : "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=800&auto=format&fit=crop&q=80";

  // Layout A: Bento Multi-Tile Hero
  if (archKey === "bento_showcase" || archKey === "asymmetric_duo") {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 select-none font-sans">
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-4 p-4 rounded-[34px] ${lightBg} border-2 ${borderCol} shadow-xl`}>
          <div className="md:col-span-7 bg-white rounded-3xl p-6 sm:p-8 border border-slate-100 flex flex-col justify-between space-y-4 text-left shadow-xs">
            <div className="space-y-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${lightBg} ${textCol} text-[10px] font-black uppercase tracking-widest border ${borderCol}`}>
                <Sparkles className="w-3.5 h-3.5" /> {badge}
              </span>
              <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight leading-tight">
                {title}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{subtitle}</p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Link href={targetUrl} className={`px-6 py-3 rounded-2xl bg-gradient-to-r ${grad} text-white font-black text-xs uppercase tracking-wider shadow-md inline-flex items-center gap-2`}>
                <span>{btnText}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-xs font-mono font-bold text-slate-400">#{variantNumber} BENTO</span>
            </div>
          </div>

          <div className="md:col-span-5 grid grid-rows-3 gap-3">
            <div className={`row-span-2 relative rounded-3xl bg-gradient-to-br ${grad} p-4 flex items-center justify-center overflow-hidden shadow-lg min-h-[170px]`}>
              <Image src={imgUrl} alt={title} fill unoptimized className="object-contain p-3 drop-shadow-2xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white border border-slate-100 p-3 flex flex-col justify-center items-center text-center">
                <span className={`text-lg font-black font-mono ${textCol}`}>4.9★</span>
                <span className="text-[9px] font-bold uppercase text-slate-400">Customer Rating</span>
              </div>
              <div className="rounded-2xl bg-slate-900 text-white p-3 flex flex-col justify-center items-center text-center">
                <span className="text-lg font-black font-mono text-amber-400">24H</span>
                <span className="text-[9px] font-bold uppercase text-slate-300">Express Dispatch</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Layout B: Perforated VIP Ticket Hero
  if (archKey === "ticket_vip" || archKey === "origami_ribbon") {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 select-none font-sans">
        <div className={`relative bg-white rounded-3xl border-2 ${borderCol} shadow-xl overflow-hidden grid grid-cols-1 md:grid-cols-12`}>
          <div className={`md:col-span-8 p-6 sm:p-10 text-left space-y-4 ${lightBg}`}>
            <div className={`inline-flex items-center gap-2 px-4 py-1 bg-gradient-to-r ${grad} text-white text-[10px] font-mono font-black uppercase tracking-widest rounded-lg shadow-xs`}>
              <Ticket className="w-3.5 h-3.5" /> VIP PASS #{variantNumber} • {palName}
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-slate-900 leading-tight">{title}</h1>
            <p className="text-xs sm:text-sm text-slate-600 max-w-lg">{subtitle}</p>
            <Link href={targetUrl} className={`px-6 py-3 rounded-xl ${btnCls} text-white font-black text-xs uppercase tracking-wider inline-flex items-center gap-2 shadow-md`}>
              <span>{btnText}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className={`md:col-span-4 relative bg-gradient-to-br ${grad} p-6 flex flex-col items-center justify-center border-t-2 md:border-t-0 md:border-l-2 border-dashed border-white/50 min-h-[220px]`}>
            <div className="relative w-44 h-44">
              <Image src={imgUrl} alt={title} fill unoptimized className="object-contain drop-shadow-2xl" />
            </div>
            <span className="mt-2 px-3 py-0.5 rounded-full bg-black/40 text-white text-[10px] font-mono font-bold uppercase">
              ADMIT ONE • LIMITED DROP
            </span>
          </div>
        </div>
      </section>
    );
  }

  // Layout C: Dark Cyber HUD / Podium Spotlight / Luxury Magazine
  if (archKey === "cyber_hud" || archKey === "podium_spotlight" || archKey === "magazine_cover") {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 select-none font-sans">
        <div className="relative rounded-[34px] bg-[#0a0b12] text-white border border-white/15 p-6 sm:p-10 shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className={`absolute -top-20 left-1/3 w-80 h-80 rounded-full bg-gradient-to-r ${grad} opacity-25 blur-3xl pointer-events-none`} />
          <div className="md:col-span-7 space-y-4 text-left relative z-10">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-gradient-to-r ${grad} text-white text-[10px] font-mono font-black uppercase tracking-widest`}>
              <Zap className="w-3.5 h-3.5 fill-current" /> {badge}
            </span>
            <h1 className={`text-2xl sm:text-5xl font-black tracking-tight leading-tight ${archKey === "magazine_cover" ? "font-serif italic" : ""}`}>
              {title}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-lg leading-relaxed">{subtitle}</p>
            <div className="pt-2 flex items-center gap-3">
              <Link href={targetUrl} className={`px-6 py-3 rounded-full bg-gradient-to-r ${grad} text-white font-black text-xs uppercase tracking-wider shadow-lg inline-flex items-center gap-2`}>
                <span>{btnText}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-[10px] font-mono text-slate-400 uppercase">SYS.V{variantNumber}</span>
            </div>
          </div>
          <div className="md:col-span-5 relative z-10 flex items-center justify-center">
            <div className="relative w-56 h-56 rounded-full bg-white/5 border border-white/15 flex items-center justify-center p-4 shadow-2xl">
              <div className={`absolute bottom-3 w-40 h-8 rounded-full bg-gradient-to-r ${grad} blur-md opacity-60`} />
              <Image src={imgUrl} alt={title} fill unoptimized className="object-contain p-3 drop-shadow-[0_20px_25px_rgba(0,0,0,0.85)]" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Layout D: Cathedral Arch / Polaroid / Circular Portal / Clay / Wave
  const frameShape =
    archKey === "cathedral_arch"
      ? "rounded-t-[110px] rounded-b-3xl"
      : archKey === "circular_portal"
      ? "rounded-full aspect-square max-w-[240px]"
      : archKey === "polaroid_collage"
      ? "rounded-2xl p-3 bg-white shadow-xl -rotate-2"
      : "rounded-[32px]";

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 select-none font-sans">
      <div className={`relative rounded-[34px] overflow-hidden border-2 ${borderCol} bg-white p-6 sm:p-10 shadow-xl grid grid-cols-1 md:grid-cols-12 gap-6 items-center`}>
        <div
          className={`absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl ${grad} opacity-15 pointer-events-none`}
          style={{ clipPath: "polygon(25% 0, 100% 0, 100% 100%, 0% 100%)" }}
        />
        <div className="md:col-span-7 space-y-4 relative z-10 text-left">
          <div className={`inline-flex items-center gap-2 px-3.5 py-1 rounded-full ${lightBg} ${textCol} border ${borderCol} text-[10px] font-black uppercase tracking-widest`}>
            <Flame className="w-3.5 h-3.5 fill-current" />
            <span>{badge}</span>
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight">
            {title}
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl leading-relaxed">{subtitle}</p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link href={targetUrl} className={`px-6 py-3 rounded-2xl bg-gradient-to-r ${grad} text-white font-black text-xs uppercase tracking-wider shadow-lg inline-flex items-center gap-2`}>
              <span>{btnText}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <div className={`px-4 py-2.5 rounded-2xl ${lightBg} ${textCol} border ${borderCol} text-xs font-bold flex items-center gap-1.5`}>
              <ShieldCheck className="w-4 h-4" />
              <span>Verified Store</span>
            </div>
          </div>
        </div>
        <div className="md:col-span-5 flex items-center justify-center relative z-10">
          <div className={`relative w-full h-56 sm:h-64 bg-gradient-to-br ${grad} ${frameShape} p-4 flex items-center justify-center shadow-2xl overflow-hidden`}>
            <Image src={imgUrl} alt={title} fill unoptimized className="object-contain p-3 drop-shadow-2xl" />
            <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-white/90 text-slate-900 text-[10px] font-mono font-black flex items-center gap-1 shadow-xs">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> #{variantNumber}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
